import React from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";
import { Button } from "../ui/button";
import { Calendar } from "../ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { RotateCcw, Calendar as CalendarIcon, MapPin } from "lucide-react";
import { format } from "date-fns";

export const ReturnTripSection = ({
  includeReturn,
  setIncludeReturn,
  returnDate,
  setReturnDate,
  returnTime,
  setReturnTime,
  pickupDate,
  pickupLocation,
  dropoffLocation
}) => {
  return (
    <Card className="dashboard-card border-primary/30">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <RotateCcw className="h-5 w-5 text-primary" />
          Return Trip
        </CardTitle>
        <CardDescription>
          Add a return trip to save time - pickup and dropoff will be swapped
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <Label htmlFor="include-return" className="font-medium">Include Return Trip</Label>
            <p className="text-sm text-muted-foreground">
              Create a return booking automatically
            </p>
          </div>
          <Switch
            id="include-return"
            checked={includeReturn}
            onCheckedChange={setIncludeReturn}
            data-testid="return-trip-toggle"
          />
        </div>

        {includeReturn && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-black/10">
            <div className="space-y-2">
              <Label>Return Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal bg-secondary/50 border-black/10"
                    data-testid="return-date-btn"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {returnDate ? format(returnDate, "PPP") : "Select return date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={returnDate}
                    onSelect={setReturnDate}
                    disabled={(d) => {
                      const pickupDateOnly = new Date(pickupDate);
                      pickupDateOnly.setHours(0, 0, 0, 0);
                      const checkDate = new Date(d);
                      checkDate.setHours(0, 0, 0, 0);
                      return checkDate < pickupDateOnly;
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2">
              <Label htmlFor="return-time">Return Time</Label>
              <Input
                id="return-time"
                type="time"
                value={returnTime}
                onChange={(e) => setReturnTime(e.target.value)}
                className="bg-secondary/50 border-black/10"
                data-testid="return-time-input"
              />
            </div>

            <div className="col-span-full p-3 bg-background/50 rounded-lg">
              <p className="text-sm font-medium mb-2 text-primary">Return Trip Preview:</p>
              <div className="text-sm space-y-1">
                <div className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 text-green-500 mt-0.5" />
                  <span className="text-muted-foreground">Pickup: {dropoffLocation || "Same as outbound dropoff"}</span>
                </div>
                <div className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 text-red-500 mt-0.5" />
                  <span className="text-muted-foreground">Dropoff: {pickupLocation || "Same as outbound pickup"}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
